<template>
  <q-dialog v-model="visible">
    <q-card style="max-width: 800px !important; width: 800px !important">
      <q-toolbar class="auto-height horizontal-line">
        <q-toolbar-title class="q-pa-sm">{{ modalTitle }}</q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onCancel" />
      </q-toolbar>
      <q-card-section class="q-pa-md horizontal-line">
        <CLoading :loading="loading" />
        <div class="text-subtitle2 text-bold q-mb-sm q-ml-sm" v-if="!isDraft">複製元</div>
        <div class="row horizontal-line">
          <div class="text-caption col q-pl-sm">リッチメニュー名</div>
          <div class="text-caption col" v-if="!isDraft">リッチメニューアセット名</div>
          <div class="text-caption col-2">作成日</div>
          <div class="text-caption col-2">作成者</div>
        </div>
        <div class="row q-mt-sm q-py-sm selected-menu">
          <div class="text-subtitle2 text-bold col q-pl-sm">{{ destinationMenuName }}</div>
          <div class="text-subtitle2 text-bold col q-pl-sm" v-if="!isDraft">{{ menuData.label }}</div>
          <div class="text-caption col-2">{{ menuData.created_at }}</div>
          <div class="text-caption col-2">{{ menuData.created_by }}</div>
        </div>
        <div class="row" v-if="isDraft">
          <div class="text-caption col q-mt-sm">※リッチメニュー複製の前の現在編集中の内容を保存してください。</div>
        </div>
        <template v-if="!isDraft && step === 1">
          <div class="row justify-center">
            <q-icon name="keyboard_double_arrow_down" size="16px" class="q-my-sm" />
          </div>
          <q-separator />
          <div class="text-subtitle2 text-bold q-my-sm q-ml-sm">複製先</div>
          <div class="text-subtitle2 q-my-sm q-ml-sm">
            複製元のリッチメニューアセットを複製するリッチメニュー名を選択してください。
          </div>
          <q-input outlined dense v-model="search" label="リッチメニュー名で検索..." class="q-mt-md q-mb-sm">
            <template v-slot:append>
              <q-icon name="search" color="text-prey-3" />
            </template>
          </q-input>
          <div class="row horizontal-line">
            <div class="text-caption col q-pl-sm">リッチメニュー名</div>
            <div class="text-caption col-2">作成日</div>
            <div class="text-caption col-2">作成者</div>
          </div>
          <template v-for="(menu, index) in richMenusFilter" :key="index">
            <div
              class="row q-mt-sm q-py-sm"
              :class="selectedMenu._id === menu._id ? 'selected-menu' : ''"
              @click="onSelectMenu(menu)"
            >
              <div class="text-subtitle2 text-bold col q-pl-sm">{{ menu.name }}</div>
              <div class="text-caption col-2">{{ menu.created_at }}</div>
              <div class="text-caption col-2">{{ menu.creator_name }}</div>
            </div>
          </template>
        </template>
        <ul v-if="!isDraft && step === 2">
          <li class="text-caption">
            上記リッチメニューへメニューパターンを複製しました。複製先のリッチメニューを開き、設定内容を確認して、保存してください。
          </li>
          <li class="text-caption text-red">複製先で保存ボタンを押さなければ、複製が反映されません。</li>
        </ul>
        <q-separator class="q-mt-sm" />
        <div class="row no-wrap items-center q-mt-md">
          <q-space />
          <div class="q-gutter-sm">
            <q-btn no-caps class="btn-cancel" @click="onCancel">{{ $t('cancel') }} </q-btn>
            <q-btn no-caps class="btn-save" @click="onSubmit" :disable="!isDraft && step === 1 && !selectedMenu._id">
              {{ step === 2 ? $t('move_to_destination') : $t('save') }}
            </q-btn>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { Prop } from 'vue-property-decorator'
import { maska } from 'maska'
import { IRichMenu, IRichMenuContent } from '@/utils/types'
import CLoading from '@/components/common/ui/CLoading.vue'
import cloneDeep from 'lodash/cloneDeep'
import { ACTION_RICH_MENU } from '@/store/actions'
import { constant } from '@/utils/constants'

@Options({
  components: { CLoading },
  directives: { maska },
  emits: ['update:modelValue', 'update:submitMenu', 'update:refetchMenu'],
})
export default class CopyMenuModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  menuData!: IRichMenuContent

  @Prop()
  isDraft!: boolean

  @Prop()
  loading!: boolean

  step = 1
  search = ''
  selectedMenu: IRichMenu = {}

  get modalTitle() {
    if (this.isDraft) {
      return '編集中の内容の保存'
    } else {
      if (this.step === 1) {
        return '複製先の選択'
      }
      return '複製完了'
    }
  }

  get destinationMenuName() {
    if (!this.isDraft && this.step === 2) {
      return this.selectedMenu.name || ''
    } else {
      return '店舗用'
    }
  }

  get richMenus() {
    return cloneDeep(this.$store.getters.richMenus) || []
  }

  get richMenusFilter() {
    if (this.search) {
      return this.richMenus.filter((item) => item.name.toLowerCase().includes(this.search.toLowerCase()))
    }
    return this.richMenus
  }

  get selectedAppId() {
    return this.$route.params.app_id
  }

  onSelectMenu(menu: IRichMenu) {
    this.selectedMenu = menu
  }

  onSubmit() {
    if (this.isDraft) {
      this.$emit('update:submitMenu')
    } else {
      if (this.step === 1) {
        this.step = 2
      } else {
        this.goto('rich_menu_setting', {
          app_id: this.selectedAppId,
          rich_menu_id: this.selectedMenu._id,
          action_type: constant.ACTION_TYPE.EDIT,
        })
        this.$emit('update:refetchMenu', this.menuData)
        this.visible = false
      }
    }
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }

  async mounted() {
    await this.$store.dispatch(ACTION_RICH_MENU.LOAD_ITEMS, {
      app_id: this.selectedAppId,
    })
  }
}
</script>

<style scoped lang="scss">
.selected-menu {
  background-color: #63a9e51a;
}
ul {
  padding-left: 12px;
}
</style>
